import AccountBalanceOutlinedIcon from '@mui/icons-material/AccountBalanceOutlined';
import BoltIcon from '@mui/icons-material/Bolt';
import CreditCardOutlinedIcon from '@mui/icons-material/CreditCardOutlined';
import LockOutlineIcon from '@mui/icons-material/LockOutline';
import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import CardActionArea from '@mui/material/CardActionArea';
import CardContent from '@mui/material/CardContent';
import Divider from '@mui/material/Divider';
import Typography from '@mui/material/Typography';
import PropTypes from 'prop-types';
import React from 'react';
import TrueLinkButton from 'react/shared/components/true_link/main/TrueLinkButton';
import TrueLinkChip from 'react/shared/components/true_link/main/TrueLinkChip';
import theme from 'react/shared/theme/Theme';
import PALETTE from 'react/shared/theme/palette';
import { currencyFormatter } from 'react/shared/utils/Money';

const getSxStyles = () => ({
  sectionHeader: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    gap: '10px',
    mb: '20px',
  },
  walletContent: {
    border: `1px solid ${PALETTE.grey2}`,
    borderRadius: '10px',
    padding: '26px 30px',
    width: '100%',
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    [theme.breakpoints.down('md')]: {
      width: '100%',
      flexDirection: 'column',
      alignItems: 'flex-start',
      gap: '30px',

      '& button': {
        width: '100%',
        textAlign: 'center',
      },
    },
  },
  walletContentRight: {
    display: 'flex',
    flexDirection: { xs: 'column', md: 'row' },
    gap: '10px',
    justifyContent: { xs: 'flex-start', md: 'flex-end' },
    width: { xs: '100%', md: 'auto' },
  },
  walletContentButton: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    gap: '4px',
    justifyContent: 'center',
  },
  cardContent: {
    display: 'flex',
    flexDirection: 'column',
    gap: '20px',
    width: { xs: '100%', md: '450px' },
  },
  card: {
    background:
      'linear-gradient(107deg, rgba(31, 81, 76, 0.00) 2.71%, rgba(31, 81, 76, 0.90) 98.04%), #0D875C',
    color: 'white',
    borderRadius: '10px',
  },
  walletContentLeft: {
    display: 'flex',
    flexDirection: { xs: 'column', md: 'row' },
    alignItems: { xs: 'flex-start', md: 'center' },
    gap: { xs: '0px', md: '10px' },
  },
});

const mockCardTotal = 1500.55;
const mockWalletTotal = 3300;
const mockFirstName = 'John';
const mockCardNumberLast4 = '5555';
const mockWalletTotalLast4 = '1234';

export default function ClientViewOverviewTab({ accountId }) {
  const sxStyles = getSxStyles();

  const navigateToAccount = () => {
    window.location.href = `${RailsRoutes.select_account_path(accountId)}`;
  };

  const walletContent = (
    <Box className="wallet-content" sx={sxStyles.walletContent}>
      <Box className="wallet-content-left">
        <Typography sx={{ fontSize: '20px', color: PALETTE.grey2 }}>Available balance</Typography>
        <Box sx={sxStyles.walletContentLeft}>
          <Typography sx={{ fontSize: '36px' }}>{currencyFormatter(mockWalletTotal)} </Typography>
          <TrueLinkChip
            color="primaryLight"
            icon={<LockOutlineIcon />}
            label={`Not spendable by ${mockFirstName}`}
            sx={{ px: 1, py: 3, fontSize: '16px !important', m: '0 !important' }}
          />
        </Box>
      </Box>
      <Box className="wallet-content-right" sx={sxStyles.walletContentRight}>
        <TrueLinkButton
          style={{ padding: '8px 16px 8px 12px', fontSize: '16px' }}
          variant="primary"
        >
          <Box sx={sxStyles.walletContentButton}>
            <BoltIcon sx={{ fill: 'white' }} />
            Instant transfer to card
          </Box>
        </TrueLinkButton>
        <TrueLinkButton variant="primary">Add funds</TrueLinkButton>
      </Box>
    </Box>
  );

  const wallet = (
    <Box className="wallet-section" sx={{ mb: '36px' }}>
      <Box className="wallet-header" sx={sxStyles.sectionHeader}>
        <AccountBalanceOutlinedIcon sx={{ fontSize: '28px' }} />
        <Typography sx={{ fontSize: '28px' }}>Wallet (...{mockWalletTotalLast4})</Typography>
        <Divider flexItem orientation="vertical" sx={{ borderColor: PALETTE.grey2 }} />
        <Typography sx={{ color: PALETTE.grey2 }} variant="body2">
          Money set aside to instantly fund {mockFirstName}'s True Link Card
        </Typography>
      </Box>
      {walletContent}
    </Box>
  );

  const cardContent = (
    <Box sx={sxStyles.cardContent}>
      <CardActionArea onClick={navigateToAccount}>
        <Card sx={sxStyles.card}>
          <CardContent sx={{ pt: '75px', pb: '20px', px: '30px' }}>
            <Box sx={{ mb: '30px' }}>
              <Typography sx={{ fontSize: '24px' }}>
                {mockFirstName}’s True Link Card (...{mockCardNumberLast4})
              </Typography>
              <Typography sx={{ fontSize: '20px' }}>Money for {mockFirstName} to spend</Typography>
            </Box>
            <Box>
              <Typography sx={{ fontSize: '20px' }}>Available balance</Typography>
              <Typography sx={{ fontSize: '36px' }}>{currencyFormatter(mockCardTotal)} </Typography>
            </Box>
          </CardContent>
        </Card>
      </CardActionArea>

      <TrueLinkButton onClick={navigateToAccount} variant="primaryOutline">
        View account
      </TrueLinkButton>
    </Box>
  );

  const card = (
    <Box className="card-section" sx={sxStyles.header}>
      <Box className="card-section-header" sx={sxStyles.sectionHeader}>
        <CreditCardOutlinedIcon sx={{ fontSize: '28px' }} />
        <Typography sx={{ fontSize: '28px' }}>Card</Typography>
      </Box>
      {cardContent}
    </Box>
  );

  return (
    <div className="client-view-overview-tab">
      {wallet}
      {card}
    </div>
  );
}

ClientViewOverviewTab.propTypes = {
  accountId: PropTypes.string.isRequired,
};
